import React, { useState, useEffect } from 'react';
import { coaches, clubInfo } from '../mockData';
import { MapPin, Shield, Target, Heart } from 'lucide-react';

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const backgroundImages = [
    'https://customer-assets.emergentagent.com/job_fight-evolution-2/artifacts/w3joo9uj_IMG_5378.JPG',
    'https://customer-assets.emergentagent.com/job_fight-evolution-2/artifacts/2mic9g7j_IMG_5379.JPG',
    'https://customer-assets.emergentagent.com/job_fight-evolution-2/artifacts/odsrrqx2_IMG_5401.jpg'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-[#1a1c1b] min-h-screen pt-24 pb-20 relative overflow-hidden">
      {/* Background Slideshow */}
      <div className="fixed inset-0 z-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-30' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`Background ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-[#1a1c1b]/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
      {/* Hero Section */}
      <section className="container mx-auto px-6 mb-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="flex flex-col items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_fight-evolution-2/artifacts/yp7kukjz_Untitled%20design.png" 
              alt="NexGen MMA Logo" 
              className="h-40 w-40 object-contain rounded-full mb-4"
              style={{ mixBlendMode: 'lighten' }}
            />
            <div className="text-[#d9fb06] font-black text-3xl uppercase tracking-tight">
              NexGen MMA
            </div>
          </div>
          <h1 className="text-[#d9fb06] font-black text-5xl md:text-7xl uppercase mb-6">
            About Us
          </h1>
          <p className="text-[#dfddd6] text-xl md:text-2xl leading-relaxed">
            {clubInfo.tagline}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="bg-[#302f2c] py-16 mb-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-[#d9fb06] font-black text-4xl uppercase mb-6">
                Our Story
              </h2>
              <p className="text-[#dfddd6] text-lg mb-4 leading-relaxed">
                NexGen MMA Club was founded with a vision to bring world-class mixed martial arts training to Gurugram, Haryana. What started as a small training group has evolved into one of the region's premier combat sports academies.
              </p>
              <p className="text-[#dfddd6] text-lg leading-relaxed">
                We believe martial arts is more than just fighting—it's a way of life. Through discipline, dedication, and community, we transform beginners into confident athletes and skilled fighters.
              </p>
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-lg">
              <img
                src="https://images.unsplash.com/photo-1615117950029-db3cf44bdefa"
                alt="Training Community"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="container mx-auto px-6 mb-20">
        <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-12 text-center">
          Our Core Values
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-[#302f2c] border border-[#3f4816] p-8 text-center hover:border-[#d9fb06] transition-colors">
            <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield size={36} className="text-[#d9fb06]" />
            </div>
            <h3 className="text-[#d9fb06] font-bold text-xl uppercase mb-3">Discipline</h3>
            <p className="text-[#dfddd6] text-sm">
              Building character through consistent practice and mental fortitude
            </p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] p-8 text-center hover:border-[#d9fb06] transition-colors">
            <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target size={36} className="text-[#d9fb06]" />
            </div>
            <h3 className="text-[#d9fb06] font-bold text-xl uppercase mb-3">Excellence</h3>
            <p className="text-[#dfddd6] text-sm">
              Striving for mastery in every technique and every training session
            </p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] p-8 text-center hover:border-[#d9fb06] transition-colors">
            <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart size={36} className="text-[#d9fb06]" />
            </div>
            <h3 className="text-[#d9fb06] font-bold text-xl uppercase mb-3">Community</h3>
            <p className="text-[#dfddd6] text-sm">
              Creating a supportive family where everyone grows together
            </p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] p-8 text-center hover:border-[#d9fb06] transition-colors">
            <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield size={36} className="text-[#d9fb06]" />
            </div>
            <h3 className="text-[#d9fb06] font-bold text-xl uppercase mb-3">Safety</h3>
            <p className="text-[#dfddd6] text-sm">
              Training smart with proper techniques and supervised sessions
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-[#3f4816] py-16 mb-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center md:text-left">
              <h3 className="text-[#d9fb06] font-black text-3xl uppercase mb-4">
                Our Mission
              </h3>
              <p className="text-[#dfddd6] text-lg leading-relaxed">
                {clubInfo.mission}
              </p>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-[#d9fb06] font-black text-3xl uppercase mb-4">
                Our Vision
              </h3>
              <p className="text-[#dfddd6] text-lg leading-relaxed">
                {clubInfo.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coaches Section */}
      <section className="container mx-auto px-6 mb-20">
        <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-12 text-center">
          Meet Our Coaches
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coaches.map((coach) => (
            <div
              key={coach.id}
              className="bg-[#302f2c] border border-[#3f4816] overflow-hidden hover:border-[#d9fb06] transition-all duration-300 group"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={coach.image}
                  alt={coach.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-[#d9fb06] font-bold text-2xl mb-2">{coach.name}</h3>
                <p className="text-[#dfddd6] text-lg uppercase tracking-wide">{coach.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sanshinkan Section */}
      <section className="container mx-auto px-6 mb-20">
        <div className="bg-[#302f2c] border-2 border-[#d9fb06] p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-4">
              Backed by Sanshinkan
            </h2>
            <div className="w-24 h-1 bg-[#d9fb06] mx-auto mb-6"></div>
          </div>

          <div className="max-w-4xl mx-auto space-y-6 text-[#dfddd6] text-lg leading-relaxed">
            <div>
              <h3 className="text-[#d9fb06] font-bold text-2xl mb-3">Sanshinkan - The Legacy</h3>
              <p className="mb-4">
                Sanshinkan is the legacy of Soke Tamas Weber. Tamas Weber is a known personality in the world of martial arts. His knowledge of Karate is unique, only a few living practitioners share his experience. Tamas Weber has trained with many big names within and outside the style of Shitoryu.
              </p>
              <p className="mb-4">
                Sanshinkan in India is represented by Sensei Yashpal Singh Kalsi, one of India's most reputed martial art practitioners and coaches. Yashpal Singh Kalsi is also a leading name in the world of Mixed Martial Arts administration.
              </p>
              <p>
                In 2022, Sanshinkan has completed 50 years since it was founded by Soke Tamas Weber.
              </p>
            </div>

            {/* Meet The Team */}
            <div className="border-t border-[#3f4816] pt-8 mt-8">
              <h3 className="text-[#d9fb06] font-bold text-2xl mb-6 text-center">Meet The Team</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <div className="text-center bg-[#1a1c1b] border border-[#3f4816] p-6 hover:border-[#d9fb06] transition-colors">
                  <h4 className="text-[#d9fb06] font-bold text-xl uppercase mb-2">Soke Tamas Weber</h4>
                  <p className="text-[#888680] text-sm uppercase">Founder</p>
                </div>
                <div className="text-center bg-[#1a1c1b] border border-[#3f4816] p-6 hover:border-[#d9fb06] transition-colors">
                  <h4 className="text-[#d9fb06] font-bold text-xl uppercase mb-2">Yashpal Singh Kalsi</h4>
                  <p className="text-[#888680] text-sm uppercase">Sensei - India Representative</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="container mx-auto px-6">
        <div className="bg-[#302f2c] border border-[#3f4816] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center mb-4">
                <MapPin size={28} className="text-[#d9fb06] mr-3" />
                <h3 className="text-[#d9fb06] font-black text-3xl uppercase">
                  Visit Us
                </h3>
              </div>
              <p className="text-[#dfddd6] text-lg mb-6">
                Come experience our world-class facility in person. Drop by for a tour or book your free trial class today.
              </p>
              <div className="space-y-3 mb-6">
                <p className="text-[#dfddd6]">
                  <span className="text-[#888680]">Location:</span> {clubInfo.location}
                </p>
                <p className="text-[#dfddd6]">
                  <span className="text-[#888680]">Phone:</span> {clubInfo.phone}
                </p>
                <p className="text-[#dfddd6]">
                  <span className="text-[#888680]">Email:</span> {clubInfo.email}
                </p>
              </div>
              <a
                href={clubInfo.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#d9fb06] text-[#1a1c1b] px-8 py-3 rounded-full font-bold uppercase text-sm hover:opacity-90 transition-all hover:scale-105 inline-flex items-center justify-center self-start"
              >
                Get Directions
              </a>
            </div>
            <div className="h-96 lg:h-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224357.4431246269!2d76.84324775!3d28.52758295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x52c2b7494e204dce!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1234567890123"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="NexGen MMA Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
};

export default About;
